import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * Minus at the quantity-rule minimum sets qty to 0 (remove line) — cart-items already handles 0.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   * Minus stays enabled while qty > 0 so the next click can remove the line
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= 0;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Cart: decreasing at/below the quantity-rule min removes the line (qty 0)
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    let newValue = value + step * stepMultiplier;

    if (stepMultiplier < 0 && newValue < min) {
      newValue = 0;
    } else {
      newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, newValue));
    }

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Cart blur: 0 removes the line; otherwise snap to quantity rules
   * @param {Event} event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const parsed = parseInt(event.target.value, 10) || 0;

    if (parsed <= 0) {
      quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parsed));

    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
